import { ChangeDetectionStrategy, Component, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SeoService } from '../../shared/seo/seo.service';

/** Donde el alta web encuentra el código si la persona no se registra enseguida. */
export const CLAVE_CODIGO_INVITACION = 'aura_codigo_invitacion';

/** Mismo alfabeto que genera api-nest (sin 0/O, 1/I/L); el largo deja margen. */
const FORMATO_CODIGO = /^[A-Z0-9]{4,12}$/;

const PLAY = 'https://play.google.com/store/apps/details?id=pe.aura.app';
const APP_STORE = 'https://apps.apple.com/pe/app/id6814717304';

/**
 * Destino del enlace de invitación que comparte la app: auracentromedico.com/r/<código>.
 * Ver SPEC-036.
 *
 * El código no se valida contra el backend acá: lo valida el alta, que es donde importa,
 * y así esta página no sirve para averiguar qué códigos existen. Solo se guarda en el
 * navegador para precargarlo si la persona se registra en la web más tarde.
 */
@Component({
  selector: 'app-invitacion',
  imports: [RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    :host{ display:block; background:#eef2f8; min-height:100vh; color:#12263a;
      font-family: Manrope, Roboto, Arial, sans-serif; }
    .wrap{ max-width:520px; margin:0 auto; padding:40px 18px 60px; text-align:center; }
    h1{ font-size:1.8rem; margin:0 0 8px; }
    .lead{ color:#48586a; margin:0 0 24px; line-height:1.5; }
    .card{ background:#fff; border:1px solid #e3e9f1; border-radius:16px; padding:22px 20px; margin-bottom:16px; }
    .etiqueta{ color:#48586a; font-size:.85rem; font-weight:700; margin:0 0 6px; }
    .codigo{ font-size:2rem; font-weight:900; letter-spacing:.25em; margin:0; }
    .copiar{ margin-top:10px; background:none; border:1px solid #c9d6e6; border-radius:999px;
      padding:6px 14px; font-weight:700; color:#0d5f8f; cursor:pointer; }
    .botones{ display:flex; flex-direction:column; gap:10px; }
    .btn{ display:block; padding:14px; border-radius:12px; font-weight:800; text-decoration:none; }
    .btn.primario{ background:#0d5f8f; color:#fff; }
    .btn.secundario{ background:#fff; color:#0d5f8f; border:2px solid #0d5f8f; }
    .nota{ color:#48586a; font-size:.9rem; line-height:1.5; margin:14px 0 0; }
  `],
  template: `
    <main class="wrap">
      <h1>Te invitaron a Aura</h1>
      <p class="lead">
        Reservá spas, clínicas y salones cerca tuyo. Registrate con este código y recibí
        puntos de regalo cuando completes tu primera reserva.
      </p>

      <section class="card">
        <p class="etiqueta">Código de invitación</p>
        <p class="codigo">{{ codigo }}</p>
        <button class="copiar" type="button" (click)="copiar()">{{ copiado() ? 'Copiado' : 'Copiar código' }}</button>
      </section>

      <section class="card botones">
        <a class="btn primario" [href]="play" target="_blank" rel="noopener">Descargar en Google Play</a>
        <a class="btn primario" [href]="appStore" target="_blank" rel="noopener">Descargar en App Store</a>
        <a class="btn secundario" routerLink="/marketplace/registro" [queryParams]="{ codigo: codigo }">
          Crear mi cuenta en la web
        </a>
        <p class="nota">
          En la app, al crear tu cuenta, escribí el código en "¿Te invitó alguien?".
        </p>
      </section>
    </main>
  `,
})
export class InvitacionComponent {
  readonly play = PLAY;
  readonly appStore = APP_STORE;
  readonly copiado = signal(false);
  readonly codigo: string;

  private readonly navegador = isPlatformBrowser(inject(PLATFORM_ID));

  constructor() {
    const route = inject(ActivatedRoute);
    const router = inject(Router);
    this.codigo = (route.snapshot.paramMap.get('codigo') ?? '').trim().toUpperCase();

    inject(SeoService).set({
      titulo: 'Te invitaron a Aura',
      descripcion: 'Registrate en Aura con tu código de invitación y recibí puntos de regalo con tu primera reserva.',
      path: '/r',
      noindex: true,
    });

    if (!FORMATO_CODIGO.test(this.codigo)) {
      // Un enlace roto no deja a nadie en una página sin salida.
      void router.navigateByUrl('/marketplace');
      return;
    }
    if (this.navegador) {
      try {
        localStorage.setItem(CLAVE_CODIGO_INVITACION, this.codigo);
      } catch {
        // Navegación privada o almacenamiento bloqueado: el código viaja igual en el enlace.
      }
    }
  }

  async copiar(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.codigo);
      this.copiado.set(true);
    } catch {
      // Sin permiso de portapapeles el código sigue a la vista.
    }
  }
}
